import { reactive } from "vue";

type SessionTimeoutState = {
  baseTime: number;
  passedMsec: number;
  timeoutApproaching: boolean;
  timeout: boolean;
  logoutTimerId: ReturnType<typeof setInterval> | null;
};

type SessionTimeoutConfigState = {
  timeoutMsec: number;
  timeoutApproachingMsec: number;
};

type SessionTimeoutStartOptions = {
  isSessionTrackable: () => boolean;
  onTimeout: () => void;
  timeoutMsec?: number;
  timeoutApproachingMsec?: number;
};

/** ログインセッションは4時間で切れる 画面遷移でリフレッシュされる */
const DEFAULT_TIMEOUT_MSEC = 4 * 60 * 60 * 1000;
/** ログインセッションが切れる10分前に画面に通知を出す */
const DEFAULT_TIMEOUT_APPROACHING_MSEC = DEFAULT_TIMEOUT_MSEC - 10 * 60 * 1000;

const configState = reactive<SessionTimeoutConfigState>({
  timeoutMsec: DEFAULT_TIMEOUT_MSEC,
  timeoutApproachingMsec: DEFAULT_TIMEOUT_APPROACHING_MSEC
});

const state = reactive<SessionTimeoutState>({
  // 非アクティブ経過時間の起点になる時刻。
  baseTime: Date.now(),
  passedMsec: 0,
  timeoutApproaching: false,
  timeout: false,
  logoutTimerId: null
});

const setBaseTimeNow = (): void => {
  state.baseTime = Date.now();
  state.passedMsec = 0;
};

export const clearTimeoutApproaching = (): void => {
  state.timeoutApproaching = false;
};

export const resetSessionTimeoutActivity = (): void => {
  setBaseTimeNow();
  clearTimeoutApproaching();
};

export const hideTimeoutAlert = (): void => {
  state.timeout = false;
};

export const showTimeoutAlert = (): void => {
  state.timeout = true;
};

const countPassedMsec = (): void => {
  state.passedMsec = Date.now() - state.baseTime;
};

export const stopSessionTimeoutTimer = (): void => {
  if (state.logoutTimerId != null) {
    clearInterval(state.logoutTimerId);
    state.logoutTimerId = null;
  }
};

export const startSessionTimeoutTimer = ({
  isSessionTrackable,
  onTimeout,
  timeoutMsec,
  timeoutApproachingMsec
}: SessionTimeoutStartOptions): void => {
  const getTimeoutMsec = (): number => timeoutMsec ?? configState.timeoutMsec;
  const getTimeoutApproachingMsec = (): number => timeoutApproachingMsec ?? configState.timeoutApproachingMsec;

  stopSessionTimeoutTimer();
  state.logoutTimerId = setInterval(() => {
    // ユーザーセッションが揃うまでは監視しない。
    if (!isSessionTrackable()) return;

    countPassedMsec();
    if (!state.timeoutApproaching && state.passedMsec >= getTimeoutApproachingMsec()) {
      state.timeoutApproaching = true;
      return;
    }

    if (state.passedMsec >= getTimeoutMsec()) {
      // tick重複による二重ログアウト処理を防ぐため、先に監視タイマーを止める。
      stopSessionTimeoutTimer();
      showTimeoutAlert();
      onTimeout();
    }
  }, 1000);
};

export const setupSessionTimeoutTimer = ({
  isSessionTrackable,
  onTimeout,
  timeoutMsec,
  timeoutApproachingMsec
}: SessionTimeoutStartOptions): void => {
  hideTimeoutAlert();
  resetSessionTimeoutActivity();
  startSessionTimeoutTimer({
    isSessionTrackable,
    onTimeout,
    timeoutMsec,
    timeoutApproachingMsec
  });
};

export const sessionTimeoutState = state;
export const sessionTimeoutConfigState = configState;
